import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import HubTabsBar from '../../../components/HubTabsBar';
import { WORKFORCE_TABS } from '../../../hubs/workforceTabs';

// Lazy imports kept simple — these are admin-only pages that are already loaded for the admin shell.
import Employees from '../Employees';
import EmployeeSchedule from '../../employee/EmployeeSchedule';
import TimeClock from '../../employee/TimeClock';
import AdminTimesheets from '../AdminTimesheets';
import AdminTimeOff from '../AdminTimeOff';
import JobApprovals from '../JobApprovals';
import AdminApprovals from '../AdminApprovals';
import Vehicles from '../Vehicles';
import AdminBonusRequests from '../AdminBonusRequests';
import Payroll from '../Payroll';
import HolidaysBenefits from '../HolidaysBenefits';

// iter75 — Map tab key → page component. Pages were not refactored; they're mounted exactly as
// they exist today. Only the navigation chrome (sidebar entries) changed.
const TAB_COMPONENT = {
  'employees':         Employees,
  'my-field-schedule': EmployeeSchedule,
  'my-timesheet':      TimeClock,
  'timesheets':        AdminTimesheets,
  'time-off':          AdminTimeOff,
  'holidays':          HolidaysBenefits,
  'job-approvals':     JobApprovals,
  'approvals':         AdminApprovals,
  'vehicles':          Vehicles,
  'bonuses':           AdminBonusRequests,
  'payroll':           Payroll,
};

const WorkforceHub = () => {
  const { user, hasPermission } = useAuth();
  const role = user?.role;
  const { tabKey } = useParams();

  // iter160 — A tab is visible if the role matches OR the user has any of the
  // optional `perms` for that tab. Admin/super_admin always pass via hasPermission.
  const tabAllowed = (t) => {
    if (t.allow && t.allow.has(role)) return true;
    if (Array.isArray(t.perms) && t.perms.some((p) => hasPermission(p))) return true;
    return false;
  };
  const visibleTabs = WORKFORCE_TABS.filter(tabAllowed);
  // No accessible tabs (technicians shouldn't ever land here, but be safe)
  if (visibleTabs.length === 0) {
    return (
      <div data-testid="workforce-hub-no-access" className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">
        You don&apos;t have access to any Workforce tools.
      </div>
    );
  }

  // No tab in URL → redirect to the first allowed tab so deep-links and pinned-Favorite paths always resolve.
  if (!tabKey) {
    return <Navigate to={visibleTabs[0].path} replace />;
  }

  // Unknown tab OR tab the user can't access → fall back to the first allowed tab.
  const active = visibleTabs.find((t) => t.key === tabKey);
  if (!active) {
    return <Navigate to={visibleTabs[0].path} replace />;
  }

  const Component = TAB_COMPONENT[active.key];
  return (
    <div data-testid="workforce-hub" className="space-y-4">
      <HubTabsBar tabs={WORKFORCE_TABS} userRole={role} hasPermission={hasPermission} testIdPrefix="workforce" />
      <div data-testid={`workforce-hub-content-${active.key}`}>
        {Component ? <Component /> : <div className="text-[#A3A3A3]">Coming soon.</div>}
      </div>
    </div>
  );
};

export default WorkforceHub;
